import { useQuery, useQueryClient } from "@tanstack/react-query"
import { ArrowLeftRight } from "lucide-react"
import { useState } from "react"

import { api } from "@/api/cliente"
import { Boton } from "@/componentes/ui/Boton"
import { SelectorBuscable } from "@/componentes/ui/SelectorBuscable"

import { comoCampo } from "./conversion"
import { DialogoMovimiento } from "./DialogoMovimiento"
import { comoMonto } from "./dinero"
import type { Cuenta, Divisa } from "./tipos"

/**
 * Traer el dinero que falta SIN salir del modal de pago (19/10, pedido del usuario: casi
 * todo pago desde BBVA Provincial empieza con un cambio desde Binance).
 *
 * Devuelve dos piezas: el `aviso`, que va DENTRO del modal de pago cuando la cuenta no
 * alcanza, y el `dialogo` de transferencia, que va FUERA. ⚠ **El modal de pago se oculta
 * mientras se transfiere** (`abierto={!transfiriendo}`): los dos escuchan Escape en
 * `document`, y abiertos a la vez una pulsación los cerraría juntos. Lo tecleado en el
 * pago no se pierde: vive en el componente, no en el modal.
 *
 * La transferencia nace preparada (`TransferenciaPreparada`): hacia la cuenta que paga,
 * ligada a las órdenes y con lo que falta como lo que entra. Por omisión sale de la
 * cuenta de sobregiro —Binance—, que es de donde se adelanta el dinero.
 */
export function useTransferirFondos({
  cuentas,
  cuentaPagaId,
  faltante,
  moneda,
  ordenIds,
}: {
  cuentas: Cuenta[]
  cuentaPagaId: number | null
  /** Lo que falta en la cuenta que paga, en céntimos. `null` o ≤ 0: no se avisa. */
  faltante: bigint | null
  moneda: string
  ordenIds: number[]
}) {
  const clienteQuery = useQueryClient()
  const [transfiriendo, setTransfiriendo] = useState(false)
  const [origenId, setOrigenId] = useState<number | null>(null)
  const hayFaltante = faltante !== null && faltante > 0n && cuentaPagaId !== null
  const divisas = useQuery({
    queryKey: ["divisas"],
    queryFn: () => api.get<Divisa[]>("/api/v1/divisas"),
    enabled: hayFaltante,
  })
  const posibles = cuentas.filter((c) => c.activo && c.id !== cuentaPagaId)
  const origen =
    posibles.find((c) => c.id === origenId) ??
    posibles.find((c) => c.permite_sobregiro) ??
    posibles[0] ??
    null
  const divisaOrigen = (divisas.data ?? []).find((d) => d.id === origen?.divisa_id)?.codigo ?? ""
  const aviso = hayFaltante ? (
    <div
      className="tarjeta space-y-3 p-3 text-sm"
      style={{ borderColor: "var(--warning)" }}
      role="status"
    >
      <p>
        A la cuenta le faltan{" "}
        <strong className="tabular-nums">
          {comoMonto(faltante ?? 0n)} {moneda}
        </strong>{" "}
        para este pago. Antes que nada, trae los fondos:
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-48 flex-1">
          <label htmlFor="fondos-origen" className="mb-1 block text-xs font-medium uppercase">
            Desde
          </label>
          <SelectorBuscable
            id="fondos-origen"
            opciones={posibles.map((c) => ({
              valor: c.id,
              etiqueta: c.nombre,
              detalle: [c.banco, c.ultimos4 ? `···${c.ultimos4}` : null].filter(Boolean).join(" "),
            }))}
            valor={origen?.id ?? null}
            onCambiar={setOrigenId}
          />
        </div>
        <Boton
          variante="primario"
          disabled={origen === null || divisaOrigen === ""}
          onClick={() => setTransfiriendo(true)}
        >
          <ArrowLeftRight size={16} /> Transferir fondos
        </Boton>
      </div>
    </div>
  ) : null

  const dialogo =
    transfiriendo && origen !== null && cuentaPagaId !== null && faltante !== null ? (
      <DialogoMovimiento
        cuenta={origen}
        divisa={divisaOrigen}
        abierto
        preparar={{ destinoId: cuentaPagaId, ordenIds, entra: comoCampo(faltante) }}
        onCerrar={() => {
          setTransfiriendo(false)
          // El saldo cambió: la propuesta del pago y las cuentas se vuelven a pedir.
          clienteQuery.invalidateQueries({ queryKey: ["propuesta-pago"] })
          clienteQuery.invalidateQueries({ queryKey: ["propuesta-pago-combinado"] })
          clienteQuery.invalidateQueries({ queryKey: ["cuentas-bancarias"] })
        }}
      />
    ) : null

  return { transfiriendo, aviso, dialogo }
}
